import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useFileUrl } from "@/hooks/use-file-url";
import { type Currency, currencyAtom } from "@/lib/currency";
import { formatCompactCurrency } from "@/lib/format";
import { publicFoundersQueryOptions } from "@/lib/founders-list.query";
import { buildLocalizedSeoLinks, ogImageUrl } from "@/lib/seo";
import { xAvatarUrl } from "@/lib/validation";

type FounderListItem = {
	avatarUrl: string | null;
	userImage: string | null;
	name: string;
	slug: string;
	tagline: string | null;
	xHandle: string | null;
	stats: {
		allTimeRevenue: number;
		estimatedMrr: number;
		totalAgents: number;
	};
};

const FOUNDER_SKELETON_KEYS = [
	"founder-skel-1",
	"founder-skel-2",
	"founder-skel-3",
	"founder-skel-4",
	"founder-skel-5",
	"founder-skel-6",
];

type SortMetric = "mrr" | "revenue";

const METRIC_CONFIG: Record<
	SortMetric,
	{ label: string; value: (founder: FounderListItem) => number }
> = {
	mrr: {
		label: "Total MRR",
		value: (founder) => founder.stats.estimatedMrr,
	},
	revenue: {
		label: "Total revenue",
		value: (founder) => founder.stats.allTimeRevenue,
	},
};

const RANK_MEDALS = ["🥇", "🥈", "🥉"] as const;

export const Route = createFileRoute("/founders/")({
	component: FoundersListPage,
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(publicFoundersQueryOptions),
	head: ({ matches }) => {
		const desc =
			"Every founder behind the AI agents on AgentMRR, ranked by provider-verified MRR and total revenue across all their agents.";
		return {
			meta: [
				{ title: "Founders - AgentMRR" },
				{ name: "description", content: desc },
				{ property: "og:title", content: "Founders - AgentMRR" },
				{ property: "og:description", content: desc },
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/founders" }),
		};
	},
});

function FoundersListPage() {
	const currency = useAtomValue(currencyAtom);
	const foundersQuery = useQuery(publicFoundersQueryOptions);
	const founders = (foundersQuery.data?.founders ?? []) as FounderListItem[];
	const isLoading = foundersQuery.isLoading && founders.length === 0;
	const [metric, setMetric] = useState<SortMetric>("mrr");

	const sortedFounders = useMemo(() => {
		const value = METRIC_CONFIG[metric].value;
		return [...founders].sort((left, right) => value(right) - value(left));
	}, [founders, metric]);

	return (
		<PublicPageLayout>
			<main className="mx-auto w-full max-w-5xl pt-4 pb-16 sm:pt-6">
				<PageBreadcrumb
					items={[
						{ label: "Top Founders", to: "/" },
						{ label: "All founders" },
					]}
				/>

				<section className="mt-10 border-t border-border pt-3">
					<div className="pb-3">
						<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
							&gt; Founders
						</span>
					</div>
					<h1
						className="font-mono text-[clamp(1.75rem,4vw,2.75rem)] leading-[1.05] tracking-[-0.02em] text-foreground"
						style={{ fontWeight: 400 }}
					>
						Top founders
					</h1>
					<p className="mt-3 max-w-2xl text-base leading-[1.6] text-muted-foreground">
						Everyone shipping money-making AI agents on AgentMRR, ranked by
						verified MRR and total revenue across all their agents.
					</p>
				</section>

				<section className="mt-12">
					<div className="mb-4 flex justify-end">
						<Select
							value={metric}
							onValueChange={(next) => setMetric(next as SortMetric)}
						>
							<SelectTrigger className="w-[180px] font-mono text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="mrr">Total MRR</SelectItem>
								<SelectItem value="revenue">Total revenue</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{isLoading ? (
						<FoundersTableSkeleton />
					) : sortedFounders.length === 0 ? (
						<p className="border border-border bg-card px-4 py-8 text-center font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							No public founders yet.
						</p>
					) : (
						<FoundersTable
							founders={sortedFounders}
							metric={metric}
							currency={currency}
						/>
					)}
				</section>

				<div className="mt-16">
					<PublicFooter />
				</div>
			</main>
		</PublicPageLayout>
	);
}

function FoundersTable({
	founders,
	metric,
	currency,
}: {
	founders: FounderListItem[];
	metric: SortMetric;
	currency: Currency;
}) {
	const totalLabel = METRIC_CONFIG[metric].label;
	const avgLabel = metric === "mrr" ? "Avg MRR" : "Avg revenue";

	return (
		<div className="border border-border bg-card">
			<div className="grid grid-cols-[3rem_1fr_5rem_6.5rem_7rem] items-center gap-3 border-b border-border px-4 py-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground sm:grid-cols-[3.5rem_1fr_6rem_7rem_8rem] sm:px-5">
				<span>#</span>
				<span>Founder</span>
				<span className="text-right">Agents</span>
				<span className="text-right">{avgLabel}</span>
				<span className="text-right">{totalLabel}</span>
			</div>
			{founders.map((founder, index) => (
				<FounderRow
					key={founder.slug}
					founder={founder}
					rank={index + 1}
					metric={metric}
					currency={currency}
				/>
			))}
		</div>
	);
}

function FounderRow({
	founder,
	rank,
	metric,
	currency,
}: {
	founder: FounderListItem;
	rank: number;
	metric: SortMetric;
	currency: Currency;
}) {
	const agentCount = founder.stats.totalAgents;
	const total = METRIC_CONFIG[metric].value(founder);
	const average = agentCount > 0 ? Math.round(total / agentCount) : 0;
	const medal = RANK_MEDALS[rank - 1];

	return (
		<Link
			to="/founders/$founderSlug"
			params={{ founderSlug: founder.slug }}
			className="group grid grid-cols-[3rem_1fr_5rem_6.5rem_7rem] items-center gap-3 border-b border-border px-4 py-4 transition-colors last:border-b-0 hover:bg-brand-soft sm:grid-cols-[3.5rem_1fr_6rem_7rem_8rem] sm:px-5"
		>
			<span className="flex items-center font-mono text-base tabular-nums text-muted-foreground">
				{medal ? (
					<span
						className="text-xl leading-none"
						role="img"
						aria-label={`Rank ${rank}`}
					>
						{medal}
					</span>
				) : (
					rank
				)}
			</span>

			<div className="flex min-w-0 items-center gap-3.5">
				<FounderCardAvatar
					avatarUrl={
						founder.avatarUrl ??
						founder.userImage ??
						xAvatarUrl(founder.xHandle) ??
						null
					}
					name={founder.name}
				/>
				<div className="min-w-0">
					<p
						className="truncate text-base text-foreground transition-colors group-hover:text-brand"
						style={{ fontWeight: 400 }}
					>
						{founder.name}
					</p>
					{founder.xHandle ? (
						<p className="truncate font-mono text-sm text-muted-foreground">
							@{founder.xHandle}
						</p>
					) : null}
				</div>
			</div>

			<span className="text-right font-mono text-base tabular-nums text-foreground">
				{String(agentCount)}
			</span>
			<span className="text-right font-mono text-base tabular-nums text-muted-foreground">
				{formatCompactCurrency(average, currency)}
			</span>
			<span className="text-right font-mono text-base tabular-nums text-foreground">
				{formatCompactCurrency(total, currency)}
			</span>
		</Link>
	);
}

function FoundersTableSkeleton() {
	return (
		<div className="border border-border bg-card">
			{FOUNDER_SKELETON_KEYS.map((key) => (
				<div
					key={key}
					className="grid grid-cols-[3rem_1fr_5rem_6.5rem_7rem] items-center gap-3 border-b border-border px-4 py-4 last:border-b-0 sm:grid-cols-[3.5rem_1fr_6rem_7rem_8rem] sm:px-5"
				>
					<div className="h-4 w-5 animate-pulse bg-muted" />
					<div className="flex items-center gap-3.5">
						<div className="size-12 shrink-0 animate-pulse border border-border bg-muted" />
						<div className="space-y-1.5">
							<div className="h-4 w-28 animate-pulse bg-muted" />
							<div className="h-3 w-16 animate-pulse bg-muted" />
						</div>
					</div>
					<div className="ml-auto h-4 w-8 animate-pulse bg-muted" />
					<div className="ml-auto h-4 w-12 animate-pulse bg-muted" />
					<div className="ml-auto h-4 w-14 animate-pulse bg-muted" />
				</div>
			))}
		</div>
	);
}

function FounderCardAvatar({
	avatarUrl,
	name,
}: {
	avatarUrl: string | null;
	name: string;
}) {
	const { url: resolvedAvatarUrl } = useFileUrl(avatarUrl);
	const [failedAvatarUrl, setFailedAvatarUrl] = useState<string | null>(null);

	if (resolvedAvatarUrl && failedAvatarUrl !== resolvedAvatarUrl) {
		return (
			<img
				src={resolvedAvatarUrl}
				alt={name}
				className="size-12 shrink-0 border border-border bg-muted object-cover"
				onError={() => setFailedAvatarUrl(resolvedAvatarUrl)}
				referrerPolicy="no-referrer"
			/>
		);
	}

	return (
		<div
			className="inline-flex size-12 shrink-0 items-center justify-center border border-border bg-muted font-mono text-base uppercase text-foreground"
			style={{ fontWeight: 400 }}
		>
			{name
				.split(/\s+/)
				.filter(Boolean)
				.slice(0, 2)
				.map((part) => part[0]?.toUpperCase())
				.join("")}
		</div>
	);
}
